/**
 * LeadIntelligenceCard — IA comercial de un lead.
 *
 * Una sola tarjeta sobre GET/POST /leads/{id}/conversation-intelligence. El
 * backend hace UNA inferencia y devuelve todo junto: resumen,
 * cualificación, puntuación con motivos, objetivo de la siguiente
 * conversación y borrador. Aquí no se llama a la IA dos veces ni se pide
 * nada por separado.
 *
 * Lo que esta tarjeta NO hace, y no es un detalle de implementación sino lo
 * que se aprobó: no envía WhatsApp, no cambia la fase del lead y no toca la
 * próxima acción. Todo lo que pinta es una recomendación; quien decide es
 * la persona que la lee.
 *
 * De un vistazo: puntuación, banda, la línea de cualificación, el hueco que
 * toca cubrir, el objetivo y el borrador. El resto —motivos de la
 * puntuación, campos conocidos, objeciones, riesgos— vive detrás de "Ver
 * análisis": una tarjeta que lo enseña todo a la vez deja de leerse.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Sparkle, ArrowsClockwise, WarningCircle, CaretDown, CaretUp, Copy, Check,
} from "@phosphor-icons/react";
import { formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusLabel } from "@/lib/leadStatuses";
import {
  getLeadIntelligence, generateLeadIntelligence,
} from "@/lib/leadIntelligenceApi";
import {
  bandMeta, decisionMeta, reviewReasonText, qualificationLine, knownFields,
  nextGap, scoreValue,
} from "@/lib/salesBrain";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function pct(confidence) {
  const n = Number(confidence);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${Math.round(n * 100)}%`;
}

function Row({ label, children, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-0.5">
        {label}
      </div>
      <div className="text-sm text-stone-800 dark:text-stone-200 break-words">{children}</div>
    </div>
  );
}

function BulletList({ items, render }) {
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-stone-700 dark:text-stone-300 flex gap-1.5 min-w-0">
          <span className="text-stone-400 shrink-0">·</span>
          <span className="break-words min-w-0">{render(item)}</span>
        </li>
      ))}
    </ul>
  );
}

/** El botón de copiar, con su acuse. Copiar no es enviar. */
function CopyDraftButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [text]);
  return (
    <button
      type="button"
      className="dv-btn dv-btn-outline min-h-[44px] sm:min-h-0"
      onClick={copy}
      data-testid="brain-draft-copy"
    >
      {copied
        ? (<><Check size={13} weight="bold" /> Copiado</>)
        : (<><Copy size={13} /> Copiar borrador</>)}
    </button>
  );
}

export default function LeadIntelligenceCard({ leadId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin"; // rol real (mismo gating que backend)
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [actionError, setActionError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setState(await getLeadIntelligence(leadId));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getLeadIntelligence(leadId);
        if (!cancelled) setState(data);
      } catch (e) {
        if (!cancelled) setError(formatError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const analyze = useCallback(async (force = false) => {
    setAnalyzing(true);
    setActionError("");
    try {
      const data = await generateLeadIntelligence(leadId, { force: Boolean(force) });
      if (data && data.ok === false) {
        setActionError("No hay mensajes de WhatsApp que analizar todavía.");
        setState((prev) => ({ ...(prev || {}), ...data }));
      } else {
        setState(data);
      }
    } catch (e) {
      setActionError(formatError(e));
    } finally {
      setAnalyzing(false);
    }
  }, [leadId]);

  if (loading) {
    return (
      <div className="dv-card" data-testid="ci-card">
        <div className="text-xs text-stone-400" data-testid="ci-loading">
          Cargando análisis…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dv-card space-y-2" data-testid="ci-card">
        <div className="text-xs text-red-600" data-testid="ci-error">{error}</div>
        <button type="button" className="dv-btn dv-btn-ghost" onClick={load} data-testid="ci-retry">
          <ArrowsClockwise size={13} /> Reintentar
        </button>
      </div>
    );
  }

  const analysis = state?.analysis || null;
  const stale = Boolean(state?.stale);
  const available = Boolean(state?.available);
  const configured = state?.configured !== false;

  const analyzeButton = (label, testid, force = false) => (
    <button
      type="button"
      className="dv-btn dv-btn-primary min-h-[44px] sm:min-h-0"
      onClick={() => analyze(force)}
      disabled={analyzing || !available || !configured}
      data-testid={testid}
    >
      {analyzing
        ? (<><ArrowsClockwise size={14} className="animate-spin" /> Analizando…</>)
        : (<><Sparkle size={14} weight="fill" /> {label}</>)}
    </button>
  );

  // Estado A — nunca analizado.
  if (!analysis) {
    return (
      <div className="dv-card space-y-3" data-testid="ci-card">
        <p className="text-sm text-stone-600 dark:text-stone-300" data-testid="ci-empty">
          Analiza la conversación para cualificar al lead, ver qué falta por
          saber y proponer la siguiente respuesta.
        </p>
        {!configured && (
          <p className="text-xs text-stone-400">La IA no está configurada en este entorno.</p>
        )}
        {configured && !available && (
          <p className="text-xs text-stone-400">Aún no hay mensajes de WhatsApp que analizar.</p>
        )}
        {analyzeButton("Analizar conversación", "ci-analyze")}
        {actionError && (
          <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700" data-testid="ci-action-error">
            {actionError}
          </div>
        )}
      </div>
    );
  }

  const score = analysis.score || null;
  const value = scoreValue(analysis);
  const band = bandMeta(score?.band);
  const line = qualificationLine(analysis.qualification);
  const gap = nextGap(analysis.missing_information);
  const goal = analysis.next_conversation_goal || null;
  const reply = analysis.reply || {};
  const actions = Array.isArray(analysis.actions) ? analysis.actions : [];
  // El snapshot 3B sirve también al Auto Reply: `reply.text` puede anunciar
  // una acción que el motor autónomo sí ejecutará. Esta tarjeta no ejecuta
  // acciones; solo deja copiar texto. Por eso usa la variante manual. Para
  // snapshots legacy sin acciones, el texto antiguo sigue siendo válido.
  // Desde v8 `reply.text` es la respuesta COMPUESTA del bot (con sus frases
  // de traspaso «se lo paso al equipo»); a una persona le sirve el borrador
  // del modelo, `reply.model_text`.
  const manualReplyText = reply.manual_text
    || (actions.length === 0 ? (reply.model_text || reply.text) : null);
  const decision = decisionMeta(analysis.decision);
  const reviewReasons = (analysis.review_reasons || [])
    .map(reviewReasonText).filter(Boolean);
  const known = knownFields(analysis.qualification);
  const suggestion = analysis.recommended_next_action || {};
  const recStage = analysis.recommended_stage || {};
  const coverage = state?.coverage || {};

  return (
    <div className="dv-card space-y-4" data-testid="ci-card">
      {/* Hay mensajes nuevos desde el último análisis. */}
      {stale && (
        <div
          className="text-xs px-2 py-1.5 rounded border bg-amber-50 border-amber-200 text-amber-800 flex items-center gap-1.5"
          data-testid="ci-stale"
        >
          <WarningCircle size={14} weight="fill" className="shrink-0" />
          Hay mensajes nuevos desde este análisis.
        </div>
      )}

      {/* --- Cabecera: el vistazo ------------------------------------- */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {/* Sin puntuación (snapshot de un esquema anterior) no se inventa
              un número: se dice que falta y se ofrece recalcular. */}
          {value === null ? (
            <span className="text-sm text-stone-500" data-testid="brain-score-missing">
              Análisis anterior sin puntuación — actualízalo para verla.
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-2 text-sm px-2.5 py-1 rounded-full border ${band.cls}`}
              data-testid="brain-score"
            >
              <strong className="text-base leading-none">{value}</strong>
              <span className="text-stone-400 dark:text-stone-500">·</span>
              <span data-testid="brain-band">{band.label}</span>
            </span>
          )}
          {line && (
            <p className="text-sm text-stone-700 dark:text-stone-300 mt-1.5 break-words"
               data-testid="brain-qualification-line">
              {line}
            </p>
          )}
        </div>
        {analyzeButton(
          stale ? "Actualizar análisis" : (isAdmin ? "Regenerar análisis" : "Actualizar"),
          "ci-update",
          isAdmin && !stale,
        )}
      </div>

      {actionError && (
        <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700" data-testid="ci-action-error">
          {actionError}
        </div>
      )}

      {/* --- Hueco + objetivo: una cosa cada uno ----------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Row label="Falta conocer" testid="brain-gap">
          {gap ? gap.label : "Nada pendiente por cualificar."}
        </Row>
        <Row label="Siguiente objetivo" testid="brain-goal">
          {goal?.label || "—"}
        </Row>
      </div>

      {/* --- Borrador -------------------------------------------------- */}
      <div className="rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/40 p-3 space-y-2 min-w-0"
           data-testid="brain-draft-block">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400">
            Borrador
          </span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${decision.cls}`}
                data-testid="brain-decision">
            {decision.label}
          </span>
          {pct(reply.confidence) && (
            <span className="text-[11px] text-stone-400" data-testid="brain-draft-confidence">
              confianza {pct(reply.confidence)}
            </span>
          )}
        </div>

        {manualReplyText ? (
          <p className="text-sm text-stone-800 dark:text-stone-200 whitespace-pre-wrap break-words"
             data-testid="brain-draft-text">
            {manualReplyText}
          </p>
        ) : (
          <p className="text-sm text-stone-500" data-testid="brain-draft-empty">
            {actions.length > 0
              ? "La respuesta automática incluye una acción. Prepara el mensaje manual desde la conversación."
              : "La IA no ha propuesto ningún texto."}
          </p>
        )}

        {reviewReasons.length > 0 && (
          <ul className="text-xs text-amber-800 dark:text-amber-300 space-y-0.5"
              data-testid="brain-review-reasons">
            {reviewReasons.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        )}
        {analysis.decision_reason && (
          <p className="text-[11px] text-stone-500 break-words" data-testid="brain-decision-reason">
            {analysis.decision_reason}
          </p>
        )}

        {manualReplyText && (
          <div className="flex flex-wrap gap-2 pt-1">
            <CopyDraftButton text={manualReplyText} />
          </div>
        )}
        <p className="text-[11px] text-stone-400">
          Borrador — no se envía solo. Lo revisas y lo mandas tú desde la
          conversación.
        </p>
      </div>

      {/* --- Ver análisis --------------------------------------------- */}
      <button
        type="button"
        className="dv-btn dv-btn-ghost min-h-[44px] sm:min-h-0"
        onClick={() => setExpanded((v) => !v)}
        data-testid="brain-toggle"
        aria-expanded={expanded}
      >
        {expanded
          ? (<><CaretUp size={13} /> Ocultar análisis</>)
          : (<><CaretDown size={13} /> Ver análisis</>)}
      </button>

      {expanded && (
        <div className="space-y-4 pt-1" data-testid="brain-detail">
          <Row label="Resumen" testid="ci-summary">{analysis.summary}</Row>

          {/* Por qué esa puntuación. Sin esto el número es una opinión. */}
          {score?.reasons?.length > 0 && (
            <Row label="Por qué esta puntuación" testid="brain-score-reasons">
              <ul className="space-y-1">
                {score.reasons.map((r, i) => (
                  <li key={i} className="flex gap-2 items-baseline min-w-0"
                      data-testid={`brain-reason-${r.factor}`}>
                    <span className={`shrink-0 tabular-nums text-xs font-medium ${
                      r.points >= 0 ? "text-emerald-700 dark:text-emerald-400"
                        : "text-red-700 dark:text-red-400"}`}>
                      {r.points >= 0 ? `+${r.points}` : r.points}
                    </span>
                    <span className="break-words min-w-0">
                      {r.label}
                      {r.evidence && (
                        <span className="block text-xs text-stone-500">{r.evidence}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {known.length > 0 && (
            <Row label="Lo que sabemos" testid="brain-known">
              <ul className="space-y-1">
                {known.map((f) => (
                  <li key={f.key} className="flex gap-2 flex-wrap min-w-0">
                    <span className="text-stone-500">{f.label}:</span>
                    <span className="break-words min-w-0">{f.value}</span>
                    {!f.explicit && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded border border-stone-300 text-stone-500 dark:border-stone-600"
                            data-testid={`brain-inferred-${f.key}`}>
                        inferido
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {analysis.missing_information?.length > 0 && (
            <Row label="Falta por saber" testid="brain-missing">
              <BulletList items={analysis.missing_information} render={(m) => m.label} />
            </Row>
          )}

          {analysis.objections?.length > 0 && (
            <Row label="Objeciones" testid="ci-objections">
              <BulletList
                items={analysis.objections}
                render={(o) => (
                  <>
                    {o.topic ? <span className="font-medium">{o.topic}: </span> : null}
                    {o.summary}
                    {o.resolved ? <span className="text-emerald-600"> (resuelta)</span> : null}
                  </>
                )}
              />
            </Row>
          )}

          {analysis.open_questions?.length > 0 && (
            <Row label="Preguntas pendientes" testid="ci-questions">
              <BulletList items={analysis.open_questions} render={(q) => q.question} />
            </Row>
          )}

          {analysis.risk_flags?.length > 0 && (
            <Row label="Riesgos" testid="brain-risks">
              <BulletList items={analysis.risk_flags} render={(r) => r} />
            </Row>
          )}

          {(suggestion.action || recStage.stage) && (
            <div className="rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/40 p-3 space-y-2">
              {suggestion.action && (
                <Row label="Sugerencia IA" testid="ci-suggestion">
                  {suggestion.action}
                  {suggestion.timing ? ` — ${suggestion.timing}` : ""}
                  {suggestion.reason && (
                    <span className="block text-xs text-stone-500 mt-0.5">{suggestion.reason}</span>
                  )}
                </Row>
              )}
              {recStage.stage && (
                <Row label="Fase sugerida" testid="ci-stage">
                  {statusLabel(recStage.stage)}
                  {pct(recStage.confidence) ? ` (${pct(recStage.confidence)})` : ""}
                  {recStage.reason && (
                    <span className="block text-xs text-stone-500 mt-0.5">{recStage.reason}</span>
                  )}
                </Row>
              )}
              <p className="text-[11px] text-stone-400">
                Sugerencia IA — no modifica el estado ni la próxima acción del CRM.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-stone-400 flex flex-wrap gap-x-3 gap-y-1" data-testid="ci-meta">
        <span>Último análisis: {fmtDate(state?.analyzed_at)}</span>
        {coverage.message_count_analyzed != null && (
          <span>
            {coverage.message_count_analyzed} mensajes analizados
            {coverage.truncated ? " (conversación larga, recortada)" : ""}
          </span>
        )}
      </div>
    </div>
  );
}
